import { TranslationFn } from '@/hooks/useTranslator';
import { Account } from '@/types/contacts';
import { simplifyTransactionStatus, Transaction, TransactionType } from '@/types/transaction';

const HEADERS: readonly string[] = [
  'ID',
  'Date',
  'Status',
  'Type',
  'Payment Type',
  'From Account',
  'From Amount',
  'From Currency',
  'To Account',
  'To Amount',
  'To Currency',
  'Initiated By',
  'Beneficiary Name',
];

export const buildTransactionsCSV = (
  transactions: readonly Transaction[],
  tr: TranslationFn,
): string => {
  const rows = transactions.map((t: Transaction): readonly string[] => [
    t.id ?? '',
    t.date ?? '',
    tr(simplifyTransactionStatus(t.status, t.completed)),
    tr(t.type),
    t.paymentType ? tr(t.paymentType) : '',
    resolveFromAccountDisplay(t, tr),
    t.fromAmount?.value != null ? String(t.fromAmount.value) : '',
    t.fromAmount?.currencyCode ?? '',
    resolveToAccountDisplay(t, tr),
    t.toAmount?.value != null ? String(t.toAmount.value) : '',
    t.toAmount?.currencyCode ?? '',
    t.initiatedBy ?? '',
    t.beneficiaryName ?? '',
  ]);

  return [HEADERS, ...rows].map(toCsvLine).join('\r\n') + '\r\n';
};

// Mirrors TransactionCellContent.tsx 'fromAccount' rendering.
const resolveFromAccountDisplay = (t: Transaction, tr: TranslationFn): string => {
  if (t.type === TransactionType.DEPOSIT && t.payInSender?.details?.senderName) {
    return String(t.payInSender.details.senderName);
  }
  return t.fromAccount ? tr(accountName(t.fromAccount)) : '';
};

// Mirrors TransactionCellContent.tsx 'toAccount' rendering.
const resolveToAccountDisplay = (t: Transaction, tr: TranslationFn): string => {
  if (t.toRecipient) {
    return tr(t.toRecipient.individualName ?? t.toRecipient.businessName ?? '');
  }
  return t.toAccount ? tr(accountName(t.toAccount)) : '';
};

const accountName = (account: Account): string => account.name;

const toCsvLine = (cells: readonly string[]): string => cells.map(escapeCell).join(',');

const escapeCell = (value: string): string => {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export const buildTransactionsCsvFilename = (now: Date = new Date()): string => {
  const pad = (n: number): string => String(n).padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  return `transactions_${stamp}.csv`;
};
